/**
 * PalettePanel (SPEC PX-4 — workflow-builder).
 *
 * The metadata-driven palette. In R1 it is non-interactive (proves the wiring).
 * In R2 each action entry is a `@dnd-kit/core` draggable carrying its action
 * `type` via `active.data.current` (the PipelinePage pattern), so dropping it on
 * the canvas inserts a new node. Adding a NEW action type to the engine's
 * ACTION_TYPES + _ACTION_MODEL_BY_TYPE makes it appear here with NO canvas code
 * change (the Flexibility AC) — the list is driven entirely by /metadata.
 */

import { useDraggable } from '@dnd-kit/core';
import { GitBranch, GripVertical, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowMetadata } from '@/types/workflow';

function DraggableAction({ type }: { type: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type}`,
    data: { paletteActionType: type },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`palette-action-${type}`}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing select-none',
        isDragging && 'opacity-50'
      )}
    >
      <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      {type}
    </div>
  );
}

interface PalettePanelProps {
  metadata?: WorkflowMetadata;
  draggable?: boolean;
}

export function PalettePanel({ metadata, draggable = false }: PalettePanelProps) {
  return (
    <aside className="w-56 shrink-0 border-l p-3 space-y-3 overflow-auto" data-testid="palette-sidebar">
      <div>
        <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Triggers</h3>
        <div className="space-y-1">
          {(metadata?.triggers ?? []).map((t) => (
            <div
              key={t.type}
              data-testid={`palette-trigger-${t.type}`}
              className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-xs"
            >
              <Zap className="h-3.5 w-3.5 text-blue-500" />
              {t.type}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Actions</h3>
        <div className="space-y-1">
          {(metadata?.actions ?? []).map((a) =>
            draggable ? (
              <DraggableAction key={a.type} type={a.type} />
            ) : (
              <div
                key={a.type}
                data-testid={`palette-action-${a.type}`}
                className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-xs"
              >
                <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                {a.type}
              </div>
            )
          )}
        </div>
      </div>
      {draggable && (
        <p className="text-[11px] text-muted-foreground pt-2">
          Drag an action onto the canvas to add a step.
        </p>
      )}
    </aside>
  );
}
